import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

// Deductions professionals have questioned from the app. Waive it (a leave
// fine day becomes "Warning", an adjustment such as a retraining fee is
// removed) or reject it; either way the reply goes to the professional.
// Waiving is blocked once that month's payroll is locked.

const GET_DISPUTES = gql`
  query GetDeductionDisputes($status: String) {
    getDeductionDisputes(status: $status) {
      status
      message
      disputes {
        id
        employeeName
        employeePhone
        penaltyLedgerId
        adjustmentId
        amount
        itemDate
        itemLabel
        reason
        status
        adminReply
        resolvedByName
        createdAt
        resolvedAt
      }
    }
  }
`;

const RESOLVE = gql`
  mutation ResolveDeductionDispute($data: ResolveDeductionDisputeInput!) {
    resolveDeductionDispute(data: $data) {
      status
      message
    }
  }
`;

const STATUS_BADGE = {
  OPEN: "bg-yellow-100 text-yellow-800",
  WAIVED: "bg-green-100 text-green-800",
  REJECTED: "bg-gray-200 text-gray-800",
};

const when = (v) =>
  v
    ? new Date(v).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

const LeaveDisputes = () => {
  const [status, setStatus] = useState("OPEN");
  const { data, loading, refetch } = useQuery(GET_DISPUTES, {
    variables: { status: status || null },
    fetchPolicy: "network-only",
  });
  const [resolve] = useMutation(RESOLVE);
  const [replying, setReplying] = useState(null); // { dispute, decision }
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");

  const disputes = data?.getDeductionDisputes?.disputes || [];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const res = await resolve({
      variables: { data: { id: replying.dispute.id, decision: replying.decision, reply } },
    });
    const result = res.data?.resolveDeductionDispute;
    if (result?.status !== "SUCCESS") {
      setError(result?.message || "Failed");
      return;
    }
    setReplying(null);
    setReply("");
    refetch();
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {[
          ["OPEN", "Open"],
          ["WAIVED", "Waived"],
          ["REJECTED", "Rejected"],
          ["", "All"],
        ].map(([v, label]) => (
          <button
            key={label}
            onClick={() => setStatus(v)}
            className={`px-3 py-1.5 rounded border text-sm ${
              status === v ? "bg-gray-800 text-white" : "bg-white hover:bg-gray-100"
            }`}
          >
            {label}
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-2">
          Questions professionals raised about a deduction from the app.
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : disputes.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing here.</p>
      ) : (
        <table className="min-w-full text-left text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="py-2 px-3">Professional</th>
              <th className="py-2 px-3">Deduction</th>
              <th className="py-2 px-3">Their reason</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {disputes.map((d) => (
              <tr key={d.id} className="border-b align-top">
                <td className="py-2 px-3">
                  <div className="font-medium text-gray-800">{d.employeeName}</div>
                  <div className="text-xs text-gray-500">{d.employeePhone}</div>
                  <div className="text-xs text-gray-400">{when(d.createdAt)}</div>
                </td>
                <td className="py-2 px-3">
                  <div className="font-semibold text-red-600">₹{d.amount}</div>
                  <div className="text-xs text-gray-500">
                    {d.itemDate} · {d.itemLabel}
                  </div>
                  <div className="text-xs text-gray-400">
                    {d.penaltyLedgerId ? "Leave fine" : "Adjustment"}
                  </div>
                </td>
                <td className="py-2 px-3 max-w-sm whitespace-pre-wrap">{d.reason}</td>
                <td className="py-2 px-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_BADGE[d.status] || ""}`}>
                    {d.status}
                  </span>
                  {d.adminReply && (
                    <div className="text-xs text-gray-600 mt-1">
                      Reply: {d.adminReply}
                      <div className="text-gray-400">
                        {d.resolvedByName} · {when(d.resolvedAt)}
                      </div>
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 whitespace-nowrap space-x-2 text-right">
                  {d.status === "OPEN" && (
                    <>
                      <button
                        onClick={() => setReplying({ dispute: d, decision: "WAIVE" })}
                        className="px-3 py-1 rounded bg-green-600 text-white text-xs"
                      >
                        Waive
                      </button>
                      <button
                        onClick={() => setReplying({ dispute: d, decision: "REJECT" })}
                        className="px-3 py-1 rounded border bg-white text-xs"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {replying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">
              {replying.decision === "WAIVE"
                ? `Waive ₹${replying.dispute.amount}`
                : `Reject - keep ₹${replying.dispute.amount}`}
            </h2>
            <p className="text-sm text-gray-600">
              {replying.dispute.employeeName}: "{replying.dispute.reason}"
            </p>
            <label className="block">
              <span className="block text-sm font-medium mb-1">Reply to the professional</span>
              <textarea
                required
                rows={3}
                className="w-full border p-2 rounded"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={
                  replying.decision === "WAIVE"
                    ? "e.g. You applied in time - fine waived."
                    : "e.g. The leave was applied 1 day before, so the short-notice fine applies."
                }
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setReplying(null)} className="px-4 py-2 border rounded">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white">
                Send
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default LeaveDisputes;
